/**
 * module-builder-form-tree.ts — dwukierunkowe tłumaczenie układu formularza widoku `form`
 * biblioteki kartoteki (`kartoteka_views.config` = `ModuleTemplateFormConfig`, pola
 * adresowane SLUGIEM) ⇄ rekurencyjne drzewo bogatego buildera (`SystemModuleBuilderPage`
 * embedded — `initialLayoutConfig` / `onSaveKartotekaLayout`, pola adresowane `fieldId`).
 *
 * RÓŻNICA względem sąsiada `module-builder-form-layout.ts` (kształt SEKCYJNY `{ sections }`
 * dla starego `ModuleBuilderPage`): TU NIE spłaszczamy drzewa. Bogaty builder i runtime
 * kartotek systemowych (`RecursiveFormRenderer`, ADR-0028) obsługują zagnieżdżone kontenery
 * (zakładki / akordeon / stepper) ORAZ elementy view-level (wyróżnik / nagłówek strony)
 * WPROST na dowolnej głębokości — spłaszczenie zgubiłoby wyróżnik-kontener i strukturę.
 * Dlatego to rekurencyjne przepisanie kluczy (lustro `translateKartotekaViewConfigToLayoutConfig`
 * z API, ale z mapą identycznościową slug→slug — panel ITE nie zna bytów tenanta, `id ≡ slug`).
 *
 * Konwencje (świadome — zgodne z tym, co czytają builder i runtime):
 *   - STYL bloku żyje w `settings.style` (builder edytuje `settings.style`, render nakłada
 *     `settings.style` na klocek). Legacy top-level `style` (zapisy starego buildera) jest
 *     przy WCZYTANIU składany do `settings.style`, żeby bogaty builder go pokazał.
 *   - SZEROKOŚĆ pola żyje w `settings.width` (nie top-level) — jedzie w `settings` bez zmian.
 *   - WIDOCZNOŚĆ WARUNKOWA: bogaty builder trzyma ją w `settings.conditionalField/Operator/Value`.
 *     Kontrakt `ModuleTemplateFormConfig` trzyma ją na TOP-LEVEL (`conditionalFieldSlug/...`).
 *     Zapis HOISTUJE settings→top-level (kanoniczny kształt, parytet z konwerterem sekcyjnym
 *     i walidatorem); odczyt składa top-level→settings (żeby edytor ją pokazał), z fallbackiem
 *     na już-w-settings (tolerancja zapisów mieszanych).
 *   - `settings` poza warunkiem przechodzi W CAŁOŚCI (label, width, style, …).
 *
 * Świeży widok (pusty config) seeduje się w `KartotekaViewFormBuilder` (jedna zakładka „Dane"
 * z sekcją wszystkich pól) — ta paczka jest czysta, bez wiedzy o polach domyślnych.
 */

import type {
  ModuleTemplateFormAccordionItem,
  ModuleTemplateFormConfig,
  ModuleTemplateFormElement,
  ModuleTemplateFormStep,
  ModuleTemplateFormTab,
} from './module-builder-views';

// ============================================================================
// Kształt drzewa bogatego buildera (`SystemModuleBuilderPage` — ADR-0028, fieldId)
// ============================================================================

export interface BuilderFormTreeElement {
  id: string;
  type: string;
  /** Tylko dla `type === 'field'` — identyfikator pola w palecie buildera (tu `id ≡ slug`). */
  fieldId?: string;
  settings?: Record<string, unknown>;
  children?: BuilderFormTreeElement[];
  tabs?: BuilderFormTreeTab[];
  accordionItems?: BuilderFormTreeAccordionItem[];
  steps?: BuilderFormTreeStep[];
}

export interface BuilderFormTreeTab {
  id: string;
  slug: string;
  title: string;
  icon?: string;
  validateOnChange?: boolean;
  blockOnError?: boolean;
  hidden?: boolean;
  sortOrder?: number;
  children: BuilderFormTreeElement[];
}

export interface BuilderFormTreeAccordionItem {
  id: string;
  title: string;
  defaultExpanded?: boolean;
  sortOrder?: number;
  children: BuilderFormTreeElement[];
}

export interface BuilderFormTreeStep {
  id: string;
  title: string;
  icon?: string;
  validation?: boolean;
  sortOrder?: number;
  children: BuilderFormTreeElement[];
}

export interface BuilderFormTreeConfig {
  elements: BuilderFormTreeElement[];
}

// ============================================================================
// LOAD: ModuleTemplateFormConfig (fieldSlug) → drzewo buildera (fieldId)
// ============================================================================

function hasKeys(obj: Record<string, unknown>): boolean {
  return Object.keys(obj).length > 0;
}

function loadElement(
  element: ModuleTemplateFormElement,
  fieldIdBySlug: ReadonlyMap<string, string>,
): BuilderFormTreeElement {
  const settings: Record<string, unknown> = { ...(element.settings ?? {}) };

  // Legacy top-level style (zapisy starego buildera) → settings.style (nowy builder + render).
  if (element.style !== undefined && settings.style === undefined) {
    settings.style = element.style;
  }
  // Warunek: top-level kontraktu → settings (edytor czyta settings), fallback na już-w-settings.
  if (
    element.conditionalFieldSlug !== undefined &&
    element.conditionalFieldSlug !== null &&
    settings.conditionalField === undefined
  ) {
    settings.conditionalField = element.conditionalFieldSlug;
  }
  if (
    element.conditionalOperator !== undefined &&
    element.conditionalOperator !== null &&
    settings.conditionalOperator === undefined
  ) {
    settings.conditionalOperator = element.conditionalOperator;
  }
  if (element.conditionalValue !== undefined && settings.conditionalValue === undefined) {
    settings.conditionalValue = element.conditionalValue;
  }

  const out: BuilderFormTreeElement = { id: element.id, type: element.type };
  if (hasKeys(settings)) out.settings = settings;

  if (element.type === 'field') {
    const ref = element.fieldSlug ?? element.fieldId;
    // Mapa identycznościowa (slug→slug); nieznany slug zostaje jako fieldId (bez utraty
    // elementu — builder pokaże pole z brakującą definicją, nie wywali układu).
    out.fieldId = (ref ? fieldIdBySlug.get(ref) : undefined) ?? ref;
  }

  if (Array.isArray(element.children)) {
    out.children = element.children.map((c) => loadElement(c, fieldIdBySlug));
  }
  if (Array.isArray(element.tabs)) {
    out.tabs = element.tabs.map((tab) => loadTab(tab, fieldIdBySlug));
  }
  if (Array.isArray(element.accordionItems)) {
    out.accordionItems = element.accordionItems.map((item) => loadAccordion(item, fieldIdBySlug));
  }
  if (Array.isArray(element.steps)) {
    out.steps = element.steps.map((step) => loadStep(step, fieldIdBySlug));
  }

  return out;
}

function loadTab(
  tab: ModuleTemplateFormTab,
  fieldIdBySlug: ReadonlyMap<string, string>,
): BuilderFormTreeTab {
  return {
    id: tab.id,
    slug: tab.slug,
    title: tab.title,
    ...(tab.icon !== undefined ? { icon: tab.icon } : {}),
    ...(tab.validateOnChange !== undefined ? { validateOnChange: tab.validateOnChange } : {}),
    ...(tab.blockOnError !== undefined ? { blockOnError: tab.blockOnError } : {}),
    ...(tab.hidden !== undefined ? { hidden: tab.hidden } : {}),
    ...(tab.sortOrder !== undefined ? { sortOrder: tab.sortOrder } : {}),
    children: (tab.children ?? []).map((c) => loadElement(c, fieldIdBySlug)),
  };
}

function loadAccordion(
  item: ModuleTemplateFormAccordionItem,
  fieldIdBySlug: ReadonlyMap<string, string>,
): BuilderFormTreeAccordionItem {
  return {
    id: item.id,
    title: item.title,
    ...(item.defaultExpanded !== undefined ? { defaultExpanded: item.defaultExpanded } : {}),
    ...(item.sortOrder !== undefined ? { sortOrder: item.sortOrder } : {}),
    children: (item.children ?? []).map((c) => loadElement(c, fieldIdBySlug)),
  };
}

function loadStep(
  step: ModuleTemplateFormStep,
  fieldIdBySlug: ReadonlyMap<string, string>,
): BuilderFormTreeStep {
  return {
    id: step.id,
    title: step.title,
    ...(step.icon !== undefined ? { icon: step.icon } : {}),
    ...(step.validation !== undefined ? { validation: step.validation } : {}),
    ...(step.sortOrder !== undefined ? { sortOrder: step.sortOrder } : {}),
    children: (step.children ?? []).map((c) => loadElement(c, fieldIdBySlug)),
  };
}

/**
 * Tłumaczy zapisany `config` widoku (`ModuleTemplateFormConfig`, `fieldSlug`) na
 * `initialLayoutConfig` bogatego buildera (drzewo `{ elements }`, `fieldId`).
 *
 * Normalizacja korzenia pod builder: builder organizuje canvas po ZAKŁADKACH na roocie
 * (`loadTabsFromConfig` szuka elementu `type='tabs'`; bez niego = „Brak zakładek").
 * Gdy config ma już element `tabs` na roocie → przechodzi bez zmian (rodzeństwo view-level,
 * np. wyróżnik, zostaje przed nim). Gdy NIE ma (legacy płaskie sekcje starego buildera) →
 * wszystkie korzenie pakujemy w jedną domyślną zakładkę.
 *
 * `defaultTabTitle` — tytuł domyślnej zakładki przy pakowaniu legacy (i18n z wołającego).
 */
export function templateFormConfigToBuilderTree(
  config: ModuleTemplateFormConfig,
  fieldIdBySlug: ReadonlyMap<string, string>,
  defaultTabTitle = 'Dane',
): BuilderFormTreeConfig {
  const roots = (Array.isArray(config.elements) ? config.elements : []).map((el) =>
    loadElement(el, fieldIdBySlug),
  );

  if (roots.some((el) => el.type === 'tabs')) {
    return { elements: roots };
  }

  return {
    elements: [
      {
        id: 'kartoteka-view-tabs-0',
        type: 'tabs',
        tabs: [
          {
            id: 'kartoteka-view-tab-data',
            slug: 'data',
            title: defaultTabTitle,
            hidden: false,
            sortOrder: 0,
            children: roots,
          },
        ],
      },
    ],
  };
}

// ============================================================================
// SAVE: drzewo buildera (fieldId) → ModuleTemplateFormConfig (fieldSlug)
// ============================================================================

function saveElement(
  element: BuilderFormTreeElement,
  fieldSlugById: ReadonlyMap<string, string>,
): ModuleTemplateFormElement {
  const { conditionalField, conditionalOperator, conditionalValue, ...restSettings } =
    element.settings ?? {};

  const out: ModuleTemplateFormElement = { id: element.id, type: element.type };
  // settings poza warunkiem przechodzi w całości (style, width, label…).
  if (hasKeys(restSettings)) out.settings = { ...restSettings };

  // Warunek: settings buildera → top-level kontraktu (kanoniczny kształt).
  if (typeof conditionalField === 'string' && conditionalField !== '') {
    out.conditionalFieldSlug = conditionalField;
    if (typeof conditionalOperator === 'string' && conditionalOperator !== '') {
      out.conditionalOperator = conditionalOperator;
    }
    if (conditionalValue !== undefined && conditionalValue !== null) {
      out.conditionalValue = conditionalValue;
    }
  }

  if (element.type === 'field') {
    const id = element.fieldId;
    out.fieldSlug = (id ? fieldSlugById.get(id) : undefined) ?? id;
  }

  if (Array.isArray(element.children)) {
    out.children = element.children.map((c) => saveElement(c, fieldSlugById));
  }
  if (Array.isArray(element.tabs)) {
    out.tabs = element.tabs.map((tab) => saveTab(tab, fieldSlugById));
  }
  if (Array.isArray(element.accordionItems)) {
    out.accordionItems = element.accordionItems.map((item) => saveAccordion(item, fieldSlugById));
  }
  if (Array.isArray(element.steps)) {
    out.steps = element.steps.map((step) => saveStep(step, fieldSlugById));
  }

  return out;
}

function saveTab(
  tab: BuilderFormTreeTab,
  fieldSlugById: ReadonlyMap<string, string>,
): ModuleTemplateFormTab {
  return {
    id: tab.id,
    slug: tab.slug,
    title: tab.title,
    ...(tab.icon !== undefined ? { icon: tab.icon } : {}),
    ...(tab.validateOnChange !== undefined ? { validateOnChange: tab.validateOnChange } : {}),
    ...(tab.blockOnError !== undefined ? { blockOnError: tab.blockOnError } : {}),
    ...(tab.hidden !== undefined ? { hidden: tab.hidden } : {}),
    ...(tab.sortOrder !== undefined ? { sortOrder: tab.sortOrder } : {}),
    children: (tab.children ?? []).map((c) => saveElement(c, fieldSlugById)),
  };
}

function saveAccordion(
  item: BuilderFormTreeAccordionItem,
  fieldSlugById: ReadonlyMap<string, string>,
): ModuleTemplateFormAccordionItem {
  return {
    id: item.id,
    title: item.title,
    ...(item.defaultExpanded !== undefined ? { defaultExpanded: item.defaultExpanded } : {}),
    ...(item.sortOrder !== undefined ? { sortOrder: item.sortOrder } : {}),
    children: (item.children ?? []).map((c) => saveElement(c, fieldSlugById)),
  };
}

function saveStep(
  step: BuilderFormTreeStep,
  fieldSlugById: ReadonlyMap<string, string>,
): ModuleTemplateFormStep {
  return {
    id: step.id,
    title: step.title,
    ...(step.icon !== undefined ? { icon: step.icon } : {}),
    ...(step.validation !== undefined ? { validation: step.validation } : {}),
    ...(step.sortOrder !== undefined ? { sortOrder: step.sortOrder } : {}),
    children: (step.children ?? []).map((c) => saveElement(c, fieldSlugById)),
  };
}

/**
 * Tłumaczy stan bogatego buildera (`layout_config` z `onSaveKartotekaLayout`, drzewo
 * `{ elements }`, `fieldId`) na `config` widoku do zapisu (`ModuleTemplateFormConfig`,
 * `fieldSlug`). Mapa identycznościowa `id ≡ slug` w panelu ITE. Elementy view-level
 * (wyróżnik / nagłówek) jako rodzeństwo `tabs` przechodzą bez zmian (rekurencyjnie).
 */
export function builderTreeToTemplateFormConfig(
  tree: BuilderFormTreeConfig,
  fieldSlugById: ReadonlyMap<string, string>,
): ModuleTemplateFormConfig {
  const roots = Array.isArray(tree.elements) ? tree.elements : [];
  return { elements: roots.map((el) => saveElement(el, fieldSlugById)) };
}
